import { DownOutlined } from '@ant-design/icons';
import { Col, DatePicker, Form, Radio, Space, TimePicker } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Flex, FormField } from 'ui';
import moment from 'utils/cops-moment';
import {
  announcementDateFormats,
  announcementMaxStartEndTimeDiff,
  announcementPublishOptions,
} from '../../../utils/app-configs';
import { getFullDateFromFormValues } from '../../../utils/helpers';

const PublishDateSelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 5px;
  margin-left: 40px;

  ${p => p.disabled && `
    opacity: .5;
    pointer-events: none;
  `}
`;

const StyledTimePicker = styled(TimePicker)`
  width: 70px;
  margin: 0 2px;
`;

const StyledFlex = styled(Flex)`
  gap: 5px;
`;

const PublishDateValidator = styled(FormField)`
  .ant-form-item-control-input {
    display: none;
  }
`;

const PublishOptionSelector = ({
  intl,
  fieldName,
  initialValue,
  disabled,
  canDraft = true,
}) => {
  const [hasEndTimeErrors, setHasEndTimeErrors] = useState(false);
  const form = Form.useFormInstance();
  const selectedPublishOption = Form.useWatch(`${fieldName}`, form);
  const endDateField = Form.useWatch(['endDate', 'date'], form);
  const endHourField = Form.useWatch(['endDate', 'hour'], form);
  const endMinuteField = Form.useWatch(['endDate', 'minute'], form);

  useEffect(() => {
    // Force validate the endDate once everything's filled in
    if (endDateField && endHourField && endMinuteField) {
      form.validateFields(['endDateValidator']);
    }
  }, [endDateField, endHourField, endMinuteField]);

  useEffect(() => {
    // Clear errors if we switch publish option
    // We have to keep track of the end dates' error state because those have more compliated
    // custom validations
    if (selectedPublishOption === announcementPublishOptions.immediateInfinite) {
      setHasEndTimeErrors(false);
    }
  }, [selectedPublishOption]);

  const validateSelectors = (_, value) => {
    if (selectedPublishOption === announcementPublishOptions.specific) {
      if (!value) {
        return Promise.reject();
      }
    }
    return Promise.resolve();
  };

  const renderCET = (value) => {
    if (value) {
      const date = getFullDateFromFormValues(value);
      return date ? <span> = {moment(date).tz('CET').format('L LT')} CET</span> : null;
    }
    return null;
  };

  return (
    <>
      <FormField
        name={fieldName}
        initialValue={initialValue}
      >
        <Radio.Group
          disabled={disabled}
        >
          <Space direction='vertical'>
            {canDraft &&
              <Radio
                value={announcementPublishOptions.draft}
                data-test-id='radiobtn-announcement-dont-publish'
              >
                {intl.formatMessage({ id: 'announcements.edit.publishOptions.DRAFT' })}
              </Radio>
            }
            <Radio
              value={announcementPublishOptions.immediateInfinite}
              data-test-id='radiobtn-announcement-publish-immediate'
            >
              {intl.formatMessage({
                id: 'announcements.edit.publishOptions.IMMEDIATE_INDEFINITE',
              })}
            </Radio>
            <Radio
              value={announcementPublishOptions.specific}
              data-test-id='radiobtn-announcement-publish-specific'
            >
              {intl.formatMessage({
                id: 'announcements.edit.publishOptions.SPECIFIC',
              })}
            </Radio>
          </Space>
        </Radio.Group>
      </FormField>
      <PublishDateSelectorWrapper
        disabled={selectedPublishOption !== announcementPublishOptions.specific}
      >
        {/* Start Date */}
        <StyledFlex aic>
          <Col span={2}>
            {intl.formatMessage({
              id: 'announcements.edit.publishOptions.START_ON',
            })}
          </Col>
          <FormField
            name={['startDate', 'date']}
            initialValue={null}
            rules={[{ validator: validateSelectors }]}
          >
            <DatePicker
              format={announcementDateFormats.date}
              placeholder='DD/MM/YYYY'
              allowClear={false}
              suffixIcon={<DownOutlined />}
              disabled={disabled}
              data-test-id='picket-announcement-start-date'
            />
          </FormField>
          {intl.formatMessage({
            id: 'announcements.edit.publishOptions.AT',
          })}
          <Flex aic>
            <FormField
              name={['startDate', 'hour']}
              initialValue={null}
              rules={[{ validator: validateSelectors }]}
            >
              <StyledTimePicker
                format={announcementDateFormats.hour}
                showNow={false}
                allowClear={false}
                placeholder='HH'
                suffixIcon={<DownOutlined />}
                disabled={disabled}
                data-test-id='picket-announcement-start-time-hour'
              />
            </FormField>
            <FormField
              name={['startDate', 'minute']}
              initialValue={null}
              rules={[{ validator: validateSelectors }]}
            >
              <StyledTimePicker
                format={announcementDateFormats.minute}
                showNow={false}
                allowClear={false}
                placeholder='mm'
                suffixIcon={<DownOutlined />}
                disabled={disabled}
                data-test-id='picket-announcement-start-time-minute'
              />
            </FormField>
            {renderCET(form.getFieldValue('startDate'))}
          </Flex>
        </StyledFlex>
        <PublishDateValidator
          name='startDateValidator'
          dependencies={[
            ['startDate', 'date'],
            ['startDate', 'hour'],
            ['startDate', 'minute'],
          ]}
          rules={[
            ({ getFieldValue }) => ({
              validator() {
                if (selectedPublishOption === announcementPublishOptions.specific) {
                  const startDateFields = getFieldValue('startDate');

                  // Any value empty
                  if (
                    !startDateFields?.date ||
                    !startDateFields?.hour ||
                    !startDateFields?.minute
                  ) {
                    return Promise.reject(new Error(intl.formatMessage({
                      id: 'rules.ANNOUNCEMENT_STARTDATE',
                    })));
                  }
                }

                return Promise.resolve();
              },
              validateTrigger: 'onSubmit',
            }),
          ]}
        />
        {/* End Date */}
        <StyledFlex aic>
          <Col span={2}>
            {intl.formatMessage({
              id: 'announcements.edit.publishOptions.END_ON',
            })}
          </Col>
          <FormField
            name={['endDate', 'date']}
            initialValue={null}
            rules={[{ validator: validateSelectors }]}
            // eslint-disable-next-line no-undefined
            validateStatus={hasEndTimeErrors ? 'error' : undefined}
          >
            <DatePicker
              format={announcementDateFormats.date}
              placeholder='DD/MM/YYYY'
              allowClear={false}
              suffixIcon={<DownOutlined />}
              disabled={disabled}
              data-test-id='picket-announcement-end-date'
            />
          </FormField>
          {intl.formatMessage({
            id: 'announcements.edit.publishOptions.AT',
          })}
          <Flex aic>
            <FormField
              name={['endDate', 'hour']}
              initialValue={null}
              rules={[{ validator: validateSelectors }]}
              // eslint-disable-next-line no-undefined
              validateStatus={hasEndTimeErrors ? 'error' : undefined}
            >
              <StyledTimePicker
                format={announcementDateFormats.hour}
                showNow={false}
                allowClear={false}
                placeholder='HH'
                suffixIcon={<DownOutlined />}
                disabled={disabled}
                data-test-id='picket-announcement-end-time-hour'
              />
            </FormField>
            <FormField
              name={['endDate', 'minute']}
              initialValue={null}
              rules={[{ validator: validateSelectors }]}
              // eslint-disable-next-line no-undefined
              validateStatus={hasEndTimeErrors ? 'error' : undefined}
            >
              <StyledTimePicker
                format={announcementDateFormats.minute}
                showNow={false}
                allowClear={false}
                placeholder='mm'
                suffixIcon={<DownOutlined />}
                disabled={disabled}
                data-test-id='picket-announcement-end-time-minute'
              />
            </FormField>
            {renderCET(form.getFieldValue('endDate'))}
          </Flex>
        </StyledFlex>
        <PublishDateValidator
          name='endDateValidator'
          dependencies={[
            ['startDate', 'date'],
            ['startDate', 'hour'],
            ['startDate', 'minute'],
            ['endDate', 'date'],
            ['endDate', 'hour'],
            ['endDate', 'minute'],
          ]}
          validateTrigger={['onChange', 'onSubmit']}
          rules={[
            ({ getFieldValue }) => ({
              // This should only run when the form is being submitted
              // -> we don't want to check if the values are empty when the user's filling it in
              validator() {
                if (selectedPublishOption === announcementPublishOptions.specific) {
                  const endDateFields = getFieldValue('endDate');

                  // Any value empty
                  if (
                    !endDateFields?.date ||
                    !endDateFields?.hour ||
                    !endDateFields?.minute
                  ) {
                    setHasEndTimeErrors(true);
                    return Promise.reject(new Error(intl.formatMessage({
                      id: 'rules.ANNOUNCEMENT_ENDDATE',
                    })));
                  }
                }

                setHasEndTimeErrors(false);
                return Promise.resolve();
              },
              validateTrigger: 'onSubmit',
            }),
            ({ getFieldValue }) => ({
              validator() {
                // If all the values are filled in the useEffect will fire this validation
                // after that every onChange (in dependency array) will fire this validation
                if (selectedPublishOption === announcementPublishOptions.specific) {
                  const endDateFields = getFieldValue('endDate');
                  if (
                    endDateFields?.date &&
                    endDateFields?.hour &&
                    endDateFields?.minute
                  ) {
                    const endDateDate = getFullDateFromFormValues(endDateFields);
                    // End date is in past
                    if (getFullDateFromFormValues(endDateFields) < moment()) {
                      setHasEndTimeErrors(true);
                      return Promise.reject(new Error(intl.formatMessage({
                        id: 'rules.ANNOUNCEMENT_ENDDATE_PAST',
                      })));
                    }

                    const startDateFields = getFieldValue('startDate');
                    const startDateDate = getFullDateFromFormValues(startDateFields);
                    // End date is sooner than start
                    if (endDateDate < startDateDate) {
                      setHasEndTimeErrors(true);
                      return Promise.reject(new Error(intl.formatMessage({
                        id: 'rules.ANNOUNCEMENT_ENDDATE_BEFORE_STARTDATE',
                      })));
                    }

                    // End date is sooner than defined minutes from start
                    const startEndTimeDiff = endDateDate.diff(startDateDate, 'minutes');
                    if (startEndTimeDiff < announcementMaxStartEndTimeDiff) {
                      setHasEndTimeErrors(true);
                      return Promise.reject(new Error(intl.formatMessage(
                        { id: 'rules.ANNOUNCEMENT_ENDDATE_MINUTES_SOONER' },
                        { minutes: announcementMaxStartEndTimeDiff },
                      )));
                    }
                  }
                }

                setHasEndTimeErrors(false);
                return Promise.resolve();
              },
              validateTrigger: 'onChange',
            }),
          ]}
        />
      </PublishDateSelectorWrapper>
    </>
  );
};

PublishOptionSelector.propTypes = {
  intl: PropTypes.object,
  fieldName: PropTypes.string,
  initialValue: PropTypes.string,
  disabled: PropTypes.bool,
  canDraft: PropTypes.bool,
};

export default injectIntl(PublishOptionSelector);
